import type {
  CalculationResult,
  GreekStateInterestCalculator,
  InterestType,
} from "@/lib/calculator";
import {
  COVID_EPIDIKIAS_SUSPENSIONS,
  LAW_EFFECTIVE_DATE_STR,
  MRO_SPREAD,
  PRE_2019_RATE,
} from "@/lib/calculator";

export const CALCULATION_SHEET_NAME = "Υπολογισμός";
export const REFERENCE_SHEET_NAME = "Αναφορές";

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const MAIN_HEADER_ROW = 12;
const FIRST_DATA_ROW = MAIN_HEADER_ROW + 1;
const REF_MRO_HEADER_ROW = 2;
const REF_MRO_FIRST_ROW = REF_MRO_HEADER_ROW + 1;
const REF_TRIGGER_HEADER_ROW = 2;
const REF_TRIGGER_FIRST_ROW = REF_TRIGGER_HEADER_ROW + 1;

export interface InterestWorkbookInput {
  principal: number;
  startDateStr: string;
  endDateStr: string;
  daysInYear: number;
  interestType: InterestType;
}

type WindowKind = "pre" | "filing" | "trigger";

interface WorkbookWindow {
  kind: WindowKind;
  label: string;
  note: string;
  legalStartSerial: number;
  legalEndSerial: number | null;
  rateDecimal: number;
  mroDecimal: number | null;
  isSuspensionWindow: boolean;
  triggerRefRow?: number;
}

interface WindowCache {
  overlapStartSerial: number | null;
  overlapEndSerial: number | null;
  days: number;
  chargeableDays: number;
  interest: number;
}

function parseIsoDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00Z`);
}

function formatGreekDate(dateStr: string): string {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
}

function formatInterestType(type: InterestType): string {
  return type === "epidikias" ? "Τόκοι επιδικίας" : "Τόκοι υπερημερίας";
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function isoToExcelSerial(dateStr: string): number {
  return dateToExcelSerial(parseIsoDate(dateStr));
}

function dateToExcelSerial(date: Date): number {
  return (date.getTime() - EXCEL_EPOCH_MS) / MS_PER_DAY;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function secureInterest(
  principal: number,
  rateDecimal: number,
  days: number,
  daysInYear: number
): number {
  const principalInt = Math.round(principal * 100);
  const ratePercent = rateDecimal * 100;
  const rateInt = Math.round(ratePercent * 10000);

  return round2(
    (principalInt * rateInt * days) / (100_000_000 * daysInYear)
  );
}

function quoteSheetName(name: string): string {
  return `'${name.replace(/'/g, "''")}'`;
}

function getSuspensionForSerial(serial: number): string | null {
  for (const interval of COVID_EPIDIKIAS_SUSPENSIONS) {
    const start = isoToExcelSerial(interval.startDate);
    const end = isoToExcelSerial(interval.endDate);
    if (serial >= start && serial < end) {
      return interval.label;
    }
  }

  return null;
}

function splitWindowsAtSuspensions(windows: WorkbookWindow[]): WorkbookWindow[] {
  const splitWindows: WorkbookWindow[] = [];

  for (const window of windows) {
    const boundaries = new Set<number>([window.legalStartSerial]);
    const legalEndSerial = window.legalEndSerial;
    const legalEndLimit = legalEndSerial ?? Number.POSITIVE_INFINITY;

    if (legalEndSerial !== null) {
      boundaries.add(legalEndSerial);
    }

    for (const interval of COVID_EPIDIKIAS_SUSPENSIONS) {
      const suspensionStart = isoToExcelSerial(interval.startDate);
      const suspensionEnd = isoToExcelSerial(interval.endDate);

      if (
        suspensionStart > window.legalStartSerial &&
        suspensionStart < legalEndLimit
      ) {
        boundaries.add(suspensionStart);
      }
      if (
        suspensionEnd > window.legalStartSerial &&
        suspensionEnd < legalEndLimit
      ) {
        boundaries.add(suspensionEnd);
      }
    }

    const sortedBoundaries = [...boundaries].sort((a, b) => a - b);

    for (let i = 0; i < sortedBoundaries.length - 1; i++) {
      const start = sortedBoundaries[i]!;
      const end = sortedBoundaries[i + 1]!;
      const suspensionReason = getSuspensionForSerial(start);

      splitWindows.push({
        ...window,
        legalStartSerial: start,
        legalEndSerial: end,
        isSuspensionWindow: suspensionReason !== null,
        note:
          suspensionReason ??
          window.note,
      });
    }

    if (legalEndSerial === null) {
      const start = sortedBoundaries[sortedBoundaries.length - 1]!;
      const suspensionReason = getSuspensionForSerial(start);

      splitWindows.push({
        ...window,
        legalStartSerial: start,
        legalEndSerial: null,
        isSuspensionWindow: suspensionReason !== null,
        note:
          suspensionReason ??
          window.note,
      });
    }
  }

  return splitWindows;
}

function buildWorkbookWindows(
  calculator: GreekStateInterestCalculator,
  input: InterestWorkbookInput,
  result: CalculationResult
): WorkbookWindow[] {
  const effectiveRates = calculator.getEffectiveRates();
  const firstGlobalTriggerDate = calculator.getFirstGlobalTriggerDate();
  const filingRateDecimal = result.filingMroRate / 100;
  const filingWindowEnd = firstGlobalTriggerDate
    ? dateToExcelSerial(firstGlobalTriggerDate)
    : null;
  const windows: WorkbookWindow[] = [
    {
      kind: "pre",
      label: "Προϊσχύον 6%",
      note: "Σταθερό επιτόκιο έως 30/04/2019.",
      legalStartSerial: isoToExcelSerial("1900-01-01"),
      legalEndSerial: isoToExcelSerial(LAW_EFFECTIVE_DATE_STR),
      rateDecimal: PRE_2019_RATE / 100,
      mroDecimal: null,
      isSuspensionWindow: false,
    },
    {
      kind: "filing",
      label: "MRO επίδοσης + 3%",
      note:
        "Το MRO της επίδοσης/έναρξης εφαρμόζεται άμεσα μόνο έως 13/09/2022.",
      legalStartSerial: isoToExcelSerial(LAW_EFFECTIVE_DATE_STR),
      legalEndSerial: filingWindowEnd,
      rateDecimal: filingRateDecimal + MRO_SPREAD / 100,
      mroDecimal: filingRateDecimal,
      isSuspensionWindow: false,
    },
  ];

  for (let i = 1; i < effectiveRates.length; i++) {
    const currentRate = effectiveRates[i]!;
    const nextRate = effectiveRates[i + 1];
    const currentIsoDate = toIsoDate(currentRate.date);

    windows.push({
      kind: "trigger",
      label: `Κλείδωμα από ${formatGreekDate(currentIsoDate)}`,
      note: "Κοινό 'κλειδωμένο' καθεστώς μετά την ενεργοποίηση του 1%.",
      legalStartSerial: dateToExcelSerial(currentRate.date),
      legalEndSerial: nextRate ? dateToExcelSerial(nextRate.date) : null,
      rateDecimal: currentRate.rate / 100,
      mroDecimal: currentRate.mroRate / 100,
      isSuspensionWindow: false,
      triggerRefRow: REF_TRIGGER_FIRST_ROW + i,
    });
  }

  // Ensure the filing window remains open-ended if the trigger table is ever empty.
  if (effectiveRates.length === 1) {
    windows[1]!.legalEndSerial = null;
  }

  // Preserve the currently calculated principal/start/end/type for cached formula results.
  void input;

  return splitWindowsAtSuspensions(windows);
}

function calculateWindowCache(
  window: WorkbookWindow,
  input: InterestWorkbookInput
): WindowCache {
  const inputStartSerial = isoToExcelSerial(input.startDateStr);
  const inputEndSerial = isoToExcelSerial(input.endDateStr);
  const legalEndSerial = window.legalEndSerial ?? inputEndSerial;
  const overlapStart = Math.max(inputStartSerial, window.legalStartSerial);
  const overlapEnd = Math.min(inputEndSerial, legalEndSerial);
  const days = Math.max(0, overlapEnd - overlapStart);
  const chargeableDays =
    input.interestType === "epidikias" && window.isSuspensionWindow
      ? 0
      : days;

  return {
    overlapStartSerial: days > 0 ? overlapStart : null,
    overlapEndSerial: days > 0 ? overlapEnd : null,
    days,
    chargeableDays,
    interest:
      chargeableDays > 0
        ? secureInterest(
            input.principal,
            window.rateDecimal,
            chargeableDays,
            input.daysInYear
          )
        : 0,
  };
}

function setCellValue(cell: any, value: unknown, numFmt?: string): void {
  cell.value = value;
  if (numFmt) {
    cell.numFmt = numFmt;
  }
}

function setFormulaCell(
  cell: any,
  formula: string,
  result: unknown,
  numFmt?: string
): void {
  cell.value = { formula, result };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
}

function applyFilledHeaderStyle(cell: any): void {
  cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F4B99" },
  };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.border = {
    top: { style: "thin", color: { argb: "FFD7E3F4" } },
    left: { style: "thin", color: { argb: "FFD7E3F4" } },
    bottom: { style: "thin", color: { argb: "FFD7E3F4" } },
    right: { style: "thin", color: { argb: "FFD7E3F4" } },
  };
}

function applyTableHeaderStyle(cell: any): void {
  cell.font = { bold: true, color: { argb: "FF1B1F23" } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE8EEF8" },
  };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.border = {
    top: { style: "thin", color: { argb: "FFBFCBDA" } },
    left: { style: "thin", color: { argb: "FFBFCBDA" } },
    bottom: { style: "thin", color: { argb: "FFBFCBDA" } },
    right: { style: "thin", color: { argb: "FFBFCBDA" } },
  };
}

function applyBodyBorder(cell: any): void {
  cell.border = {
    top: { style: "thin", color: { argb: "FFDADFE6" } },
    left: { style: "thin", color: { argb: "FFDADFE6" } },
    bottom: { style: "thin", color: { argb: "FFDADFE6" } },
    right: { style: "thin", color: { argb: "FFDADFE6" } },
  };
}

function applyInputStyle(cell: any): void {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFF7D6" },
  };
  cell.font = { bold: true };
  applyBodyBorder(cell);
}

function applySummaryValueStyle(cell: any): void {
  cell.font = { bold: true };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF0F6EC" },
  };
  applyBodyBorder(cell);
}

function buildCalculationSheet(
  workbook: any,
  input: InterestWorkbookInput,
  result: CalculationResult,
  calculator: GreekStateInterestCalculator
): void {
  const sheet = workbook.addWorksheet(CALCULATION_SHEET_NAME, {
    views: [{ state: "frozen", ySplit: MAIN_HEADER_ROW }],
  });
  const mroHistory = calculator.getMroHistory();
  const windows = buildWorkbookWindows(calculator, input, result);
  const totalRow = FIRST_DATA_ROW + windows.length;
  const refMroLastRow = REF_MRO_FIRST_ROW + mroHistory.length - 1;

  sheet.columns = [
    { key: "label", width: 23 },
    { key: "legalStart", width: 15 },
    { key: "legalEnd", width: 15 },
    { key: "calcStart", width: 15 },
    { key: "calcEnd", width: 15 },
    { key: "days", width: 10 },
    { key: "chargeableDays", width: 12 },
    { key: "mro", width: 12 },
    { key: "spread", width: 12 },
    { key: "rate", width: 12 },
    { key: "principal", width: 15 },
    { key: "interest", width: 16 },
    { key: "notes", width: 42 },
  ];

  sheet.mergeCells("A1:M1");
  sheet.getCell("A1").value = `${formatInterestType(
    input.interestType
  ).toLocaleUpperCase("el-GR")} ΔΗΜΟΣΙΟΥ`;
  sheet.getCell("A1").font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  sheet.getCell("A1").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF173A6A" },
  };
  sheet.getCell("A1").alignment = { vertical: "middle", horizontal: "center" };
  sheet.getRow(1).height = 24;

  const inputLabels = [
    ["A3", "Κεφάλαιο (EUR)"],
    ["A4", "Ημερομηνία Επίδοσης / Έναρξης Τοκοφορίας"],
    ["A5", "Ημερομηνία Λήξης / Εξόφλησης"],
    ["A6", "Βάση Έτους"],
    ["A7", "Τύπος Τόκου"],
    ["A8", "MRO κατά την επίδοση/έναρξη"],
    ["A9", "Έναρξη Ν. 4607/2019"],
    ["A10", "Πρώτη μεταβολή 1%"],
  ] as const;

  for (const [address, label] of inputLabels) {
    const cell = sheet.getCell(address);
    cell.value = label;
    cell.font = { bold: true };
  }

  setCellValue(sheet.getCell("B3"), input.principal, "#,##0.00");
  setCellValue(sheet.getCell("B4"), isoToExcelSerial(input.startDateStr), "dd/mm/yyyy");
  setCellValue(sheet.getCell("B5"), isoToExcelSerial(input.endDateStr), "dd/mm/yyyy");
  setCellValue(sheet.getCell("B6"), input.daysInYear, "0");
  setCellValue(sheet.getCell("B7"), formatInterestType(input.interestType));
  setFormulaCell(
    sheet.getCell("B8"),
    `IFERROR(LOOKUP($B$4,${quoteSheetName(
      REFERENCE_SHEET_NAME
    )}!$A$${REF_MRO_FIRST_ROW}:$A$${refMroLastRow},${quoteSheetName(
      REFERENCE_SHEET_NAME
    )}!$B$${REF_MRO_FIRST_ROW}:$B$${refMroLastRow}),"")`,
    result.filingMroRate / 100,
    "0.00%"
  );
  setCellValue(
    sheet.getCell("B9"),
    dateToExcelSerial(calculator.getLawEffectiveDate()),
    "dd/mm/yyyy"
  );
  setCellValue(
    sheet.getCell("B10"),
    calculator.getFirstGlobalTriggerDate()
      ? dateToExcelSerial(calculator.getFirstGlobalTriggerDate()!)
      : null,
    "dd/mm/yyyy"
  );

  applyInputStyle(sheet.getCell("B3"));
  applyInputStyle(sheet.getCell("B4"));
  applyInputStyle(sheet.getCell("B5"));
  applyInputStyle(sheet.getCell("B6"));
  applyInputStyle(sheet.getCell("B7"));
  applySummaryValueStyle(sheet.getCell("B8"));
  applySummaryValueStyle(sheet.getCell("B9"));
  applySummaryValueStyle(sheet.getCell("B10"));

  sheet.getCell("B6").dataValidation = {
    type: "list",
    allowBlank: false,
    formulae: ['"365,360"'],
    showErrorMessage: true,
    errorTitle: "Μη έγκυρη βάση έτους",
    error: "Επιτρέπεται μόνο 365 ή 360.",
  };
  sheet.getCell("B7").dataValidation = {
    type: "list",
    allowBlank: false,
    formulae: ['"Τόκοι υπερημερίας,Τόκοι επιδικίας"'],
    showErrorMessage: true,
    errorTitle: "Μη έγκυρος τύπος τόκου",
    error: "Επιτρέπονται μόνο τόκοι υπερημερίας ή τόκοι επιδικίας.",
  };

  sheet.getCell("E3").value = "Συνολικός Τόκος";
  sheet.getCell("E4").value = "Συνολικό Οφειλόμενο";
  sheet.getCell("E5").value = "Σημείωση";
  sheet.getCell("F5").value =
    "Οι ημερομηνίες λήξης είναι αποκλειστικές, όπως και στην εφαρμογή.";

  for (const address of ["E3", "E4", "E5"]) {
    const cell = sheet.getCell(address);
    cell.font = { bold: true };
  }

  setFormulaCell(
    sheet.getCell("F3"),
    `SUM(L${FIRST_DATA_ROW}:L${FIRST_DATA_ROW + windows.length - 1})`,
    result.totalInterest,
    "#,##0.00"
  );
  setFormulaCell(
    sheet.getCell("F4"),
    "$B$3+F3",
    result.totalAmountDue,
    "#,##0.00"
  );
  applySummaryValueStyle(sheet.getCell("F3"));
  applySummaryValueStyle(sheet.getCell("F4"));

  const tableHeaders = [
    "Κανόνας",
    "Νομική Περίοδος Από",
    "Νομική Περίοδος Έως",
    "Υπολογισμός Από",
    "Υπολογισμός Έως",
    "Ημέρες",
    "Χρεώσιμες Ημέρες",
    "MRO",
    "Περιθώριο",
    "Επιτόκιο",
    "Κεφάλαιο",
    "Τόκος Περιόδου",
    "Παρατήρηση",
  ];

  const headerRow = sheet.getRow(MAIN_HEADER_ROW);
  headerRow.values = tableHeaders;
  headerRow.height = 30;
  headerRow.eachCell((cell: any) => applyTableHeaderStyle(cell));

  for (let index = 0; index < windows.length; index++) {
    const window = windows[index]!;
    const cache = calculateWindowCache(window, input);
    const rowNumber = FIRST_DATA_ROW + index;
    const row = sheet.getRow(rowNumber);
    const windowEndExpression = `IF(C${rowNumber}="",$B$5,C${rowNumber})`;
    const overlapCondition = `MAX($B$4,B${rowNumber})<MIN($B$5,${windowEndExpression})`;

    setCellValue(row.getCell(1), window.label);
    setCellValue(row.getCell(2), window.legalStartSerial, "dd/mm/yyyy");
    setCellValue(
      row.getCell(3),
      window.legalEndSerial,
      window.legalEndSerial === null ? undefined : "dd/mm/yyyy"
    );

    setFormulaCell(
      row.getCell(4),
      `IF(${overlapCondition},MAX($B$4,B${rowNumber}),"")`,
      cache.overlapStartSerial,
      "dd/mm/yyyy"
    );
    setFormulaCell(
      row.getCell(5),
      `IF(${overlapCondition},MIN($B$5,${windowEndExpression}),"")`,
      cache.overlapEndSerial,
      "dd/mm/yyyy"
    );
    setFormulaCell(
      row.getCell(6),
      `MAX(0,MIN($B$5,${windowEndExpression})-MAX($B$4,B${rowNumber}))`,
      cache.days,
      "0"
    );
    setFormulaCell(
      row.getCell(7),
      window.isSuspensionWindow
        ? `IF($B$7="Τόκοι επιδικίας",0,F${rowNumber})`
        : `F${rowNumber}`,
      cache.chargeableDays,
      "0"
    );

    if (window.kind === "pre") {
      setCellValue(row.getCell(8), "");
      setCellValue(row.getCell(9), "");
      setFormulaCell(
        row.getCell(10),
        `IF(F${rowNumber}>0,${PRE_2019_RATE / 100},"")`,
        cache.days > 0 ? window.rateDecimal : "",
        "0.00%"
      );
    } else if (window.kind === "filing") {
      setFormulaCell(
        row.getCell(8),
        `IF(F${rowNumber}>0,$B$8,"")`,
        cache.days > 0 ? window.mroDecimal : "",
        "0.00%"
      );
      setFormulaCell(
        row.getCell(9),
        `IF(F${rowNumber}>0,${MRO_SPREAD / 100},"")`,
        cache.days > 0 ? MRO_SPREAD / 100 : "",
        "0.00%"
      );
      setFormulaCell(
        row.getCell(10),
        `IF(F${rowNumber}>0,H${rowNumber}+I${rowNumber},"")`,
        cache.days > 0 ? window.rateDecimal : "",
        "0.00%"
      );
    } else {
      const triggerRefRow = window.triggerRefRow!;
      setFormulaCell(
        row.getCell(8),
        `IF(F${rowNumber}>0,${quoteSheetName(
          REFERENCE_SHEET_NAME
        )}!$E$${triggerRefRow},"")`,
        cache.days > 0 ? window.mroDecimal : "",
        "0.00%"
      );
      setFormulaCell(
        row.getCell(9),
        `IF(F${rowNumber}>0,${MRO_SPREAD / 100},"")`,
        cache.days > 0 ? MRO_SPREAD / 100 : "",
        "0.00%"
      );
      setFormulaCell(
        row.getCell(10),
        `IF(F${rowNumber}>0,H${rowNumber}+I${rowNumber},"")`,
        cache.days > 0 ? window.rateDecimal : "",
        "0.00%"
      );
    }

    setFormulaCell(
      row.getCell(11),
      `IF(F${rowNumber}>0,$B$3,"")`,
      cache.days > 0 ? input.principal : "",
      "#,##0.00"
    );
    setFormulaCell(
      row.getCell(12),
      `IF(F${rowNumber}>0,K${rowNumber}*J${rowNumber}*(G${rowNumber}/$B$6),0)`,
      cache.interest,
      "#,##0.00"
    );
    setCellValue(row.getCell(13), window.note);

    row.eachCell((cell: any) => applyBodyBorder(cell));
    row.getCell(6).alignment = { horizontal: "right" };
    row.getCell(7).alignment = { horizontal: "right" };
    row.getCell(8).alignment = { horizontal: "right" };
    row.getCell(9).alignment = { horizontal: "right" };
    row.getCell(10).alignment = { horizontal: "right" };
    row.getCell(11).alignment = { horizontal: "right" };
    row.getCell(12).alignment = { horizontal: "right" };
    row.getCell(13).alignment = { wrapText: true };
  }

  const totalSheetRow = sheet.getRow(totalRow);
  totalSheetRow.getCell(1).value = "ΣΥΝΟΛΟ";
  totalSheetRow.getCell(1).font = { bold: true };
  totalSheetRow.getCell(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF0F6EC" },
  };
  totalSheetRow.getCell(12).font = { bold: true };
  totalSheetRow.getCell(12).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF0F6EC" },
  };
  setFormulaCell(
    totalSheetRow.getCell(12),
    `SUM(L${FIRST_DATA_ROW}:L${FIRST_DATA_ROW + windows.length - 1})`,
    result.totalInterest,
    "#,##0.00"
  );
  for (let col = 1; col <= 13; col++) {
    applyBodyBorder(totalSheetRow.getCell(col));
  }

  sheet.autoFilter = {
    from: `A${MAIN_HEADER_ROW}`,
    to: `M${FIRST_DATA_ROW + windows.length - 1}`,
  };
}

function buildReferenceSheet(
  workbook: any,
  calculator: GreekStateInterestCalculator
): void {
  const sheet = workbook.addWorksheet(REFERENCE_SHEET_NAME, {
    views: [{ state: "frozen", ySplit: 2 }],
  });
  const mroHistory = calculator.getMroHistory();
  const effectiveRates = calculator.getEffectiveRates();

  sheet.columns = [
    { key: "mroDate", width: 14 },
    { key: "mroRate", width: 12 },
    { key: "spacer", width: 4 },
    { key: "triggerDate", width: 14 },
    { key: "triggerMro", width: 12 },
    { key: "triggerRate", width: 12 },
    { key: "notes", width: 38 },
  ];

  sheet.mergeCells("A1:B1");
  sheet.getCell("A1").value = "Ιστορικό MRO";
  applyFilledHeaderStyle(sheet.getCell("A1"));
  applyFilledHeaderStyle(sheet.getCell("B1"));

  sheet.mergeCells("D1:G1");
  sheet.getCell("D1").value = "Πίνακας Κλειδωμένων Επιτοκίων";
  applyFilledHeaderStyle(sheet.getCell("D1"));
  applyFilledHeaderStyle(sheet.getCell("E1"));
  applyFilledHeaderStyle(sheet.getCell("F1"));
  applyFilledHeaderStyle(sheet.getCell("G1"));

  const headers = [
    ["A2", "Ημερομηνία MRO"],
    ["B2", "MRO"],
    ["D2", "Έναρξη"],
    ["E2", "MRO"],
    ["F2", "Επιτόκιο"],
    ["G2", "Σημείωση"],
  ] as const;

  for (const [address, label] of headers) {
    sheet.getCell(address).value = label;
    applyTableHeaderStyle(sheet.getCell(address));
  }

  for (let index = 0; index < mroHistory.length; index++) {
    const entry = mroHistory[index]!;
    const row = REF_MRO_FIRST_ROW + index;

    setCellValue(sheet.getCell(`A${row}`), isoToExcelSerial(entry.date), "dd/mm/yyyy");
    setCellValue(sheet.getCell(`B${row}`), entry.rate / 100, "0.00%");
    applyBodyBorder(sheet.getCell(`A${row}`));
    applyBodyBorder(sheet.getCell(`B${row}`));
  }

  for (let index = 0; index < effectiveRates.length; index++) {
    const entry = effectiveRates[index]!;
    const row = REF_TRIGGER_FIRST_ROW + index;
    const isoDate = toIsoDate(entry.date);

    setCellValue(sheet.getCell(`D${row}`), isoToExcelSerial(isoDate), "dd/mm/yyyy");
    setCellValue(sheet.getCell(`E${row}`), entry.mroRate / 100, "0.00%");
    setCellValue(sheet.getCell(`F${row}`), entry.rate / 100, "0.00%");
    setCellValue(
      sheet.getCell(`G${row}`),
      index === 0
        ? "Αρχική βάση του νόμου."
        : "Ενεργοποίηση σωρευτικής μεταβολής >= 1%."
    );

    applyBodyBorder(sheet.getCell(`D${row}`));
    applyBodyBorder(sheet.getCell(`E${row}`));
    applyBodyBorder(sheet.getCell(`F${row}`));
    applyBodyBorder(sheet.getCell(`G${row}`));
  }

  const legendRow = REF_TRIGGER_FIRST_ROW + effectiveRates.length + 2;
  sheet.mergeCells(`D${legendRow}:G${legendRow}`);
  sheet.getCell(
    `D${legendRow}`
  ).value =
    "Το MRO της επίδοσης/έναρξης επηρεάζει άμεσα το επιτόκιο μόνο μέχρι 13/09/2022. Από 14/09/2022 και μετά εφαρμόζεται το κοινό trigger-locked schedule.";
  sheet.getCell(`D${legendRow}`).alignment = { wrapText: true };
  sheet.getCell(`D${legendRow}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF7F8FA" },
  };
  applyBodyBorder(sheet.getCell(`D${legendRow}`));
}

export async function buildInterestWorkbookBuffer(
  input: InterestWorkbookInput,
  result: CalculationResult,
  calculator: GreekStateInterestCalculator
): Promise<ArrayBuffer> {
  const ExcelJS = await import("exceljs");
  const WorkbookCtor = (ExcelJS.Workbook ??
    ExcelJS.default?.Workbook) as new () => any;

  if (!WorkbookCtor) {
    throw new Error("Δεν ήταν δυνατή η φόρτωση της βιβλιοθήκης Excel.");
  }

  const workbook = new WorkbookCtor();
  workbook.creator = "tokoi-dimosiou";
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.calcProperties.fullCalcOnLoad = true;

  buildCalculationSheet(workbook, input, result, calculator);
  buildReferenceSheet(workbook, calculator);

  const buffer = await workbook.xlsx.writeBuffer();

  return buffer as ArrayBuffer;
}

export function createInterestWorkbookFilename(
  input: InterestWorkbookInput
): string {
  const suffix = input.interestType === "epidikias" ? "-epidikias" : "";
  return `tokoi-dimosiou-${input.startDateStr}-${input.endDateStr}${suffix}.xlsx`;
}
